import { useCallback } from 'react'
import type { CalendarEvent, DetectedMeeting, RecallDetectedMeeting } from '@highlight/common'
import { Toast, useToast } from '@renderer/overlay/Toaster'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'
import { Button } from '@highlight/ui'
import { useLiveMeetingIdentity } from '@renderer/overlay/Sidepanel/components/meeting/use-live-meeting-identity'
import { MEETING_VIEW_ID } from '@renderer/overlay/Sidepanel/components/meeting/meeting-view-id'
import { useOutOfMeetingHours } from '@renderer/overlay/Sidepanel/components/meeting/use-out-of-meeting-hours'
import { useMatchingCalendarEvent } from './hooks'
import { OutOfMeetingHoursToast } from './out-of-meeting-hours-toast'

interface RecordingStartedToastProps {
  toastId: string
  /** The meeting the start was for: the awaited calendar event, or the detected meeting that auto-started. */
  meeting?: CalendarEvent | DetectedMeeting | RecallDetectedMeeting
}

const isDetectedMeeting = (
  meeting: CalendarEvent | DetectedMeeting | RecallDetectedMeeting,
): meeting is DetectedMeeting | RecallDetectedMeeting => 'app' in meeting

export const RecordingStartedToast = ({ toastId, meeting }: RecordingStartedToastProps) => {
  const { openViewInSidepanel } = useOpenViewInSidepanel()
  // Same identity every other live entry point opens with — a Custom Meeting has
  // no calendar event, so opening by event alone would read as a different
  // document from the one Mini already opened and stack a second Meeting View.
  const liveMeeting = useLiveMeetingIdentity()
  const toast = useToast()
  const outOfHours = useOutOfMeetingHours()
  const detectedMeeting = meeting && isDetectedMeeting(meeting) ? meeting : undefined
  const matchedCalendarEvent = useMatchingCalendarEvent(detectedMeeting)
  const calendarEvent = meeting && !isDetectedMeeting(meeting) ? meeting : (matchedCalendarEvent ?? undefined)

  const handleOpenTranscript = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      openViewInSidepanel(MEETING_VIEW_ID, liveMeeting)
      toast.dismiss(toastId)
    },
    [openViewInSidepanel, liveMeeting, toast, toastId],
  )

  // This toast fires when the start is requested, before the gate has answered,
  // so out of hours it would promise a transcription that never begins.
  if (outOfHours) {
    return <OutOfMeetingHoursToast toastId={toastId} calendarEvent={calendarEvent} />
  }

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <Toast.Footer>
          <span>Transcription in progress…</span>
          <Button onClick={handleOpenTranscript} size="sm" variant="primary">
            View Transcript
          </Button>
        </Toast.Footer>
      </Toast.Content>
    </Toast>
  )
}
